import type React from 'react';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import { Button } from '@/components/atoms/Buttons';
import Field from '@/components/atoms/FormField';
import { Spinner } from '@/components/atoms/Spinner';
import TextArea from '@/components/atoms/TextArea';
import TextField from '@/components/atoms/TextField';
import { ToastSuccess } from '@/components/molecules/ToastTemplate/ToastTemplate';
import {
    type CageActiveFishGroup,
    isActiveFishGroupChangedError,
    useUpdateCageActiveFishGroup,
} from '@/domain/fishGroups';

import {
    type ActiveFishGroupFormValues,
    buildActiveFishGroupRequest,
    isFishCountValid,
    syncUntouchedFields,
    toFormValues,
} from './activeFishGroupRequest';

interface Props {
    cageId: number;
    clientId: number;
    group: CageActiveFishGroup;
}

/** Renames the cage's active fish group or corrects its fish count, with a required reason. */
export function ActiveFishGroupForm(props: Props) {
    const { cageId, clientId, group } = props;
    const { t } = useTranslation('cageSettings');
    const { t: tCommon } = useTranslation('common');

    const nameId = useId();
    const fishCountId = useId();
    const reasonId = useId();

    const [values, setValues] = useState<ActiveFishGroupFormValues>(() => toFormValues(group));
    const [error, setError] = useState<ReactNode>(null);

    // The group the inputs were last filled from. When a refetch brings new data for it, the
    // fields the user has not touched follow; the request is diffed against the latest group.
    const shownGroup = useRef(group);
    useEffect(() => {
        if (shownGroup.current === group) return;
        const previous = shownGroup.current;
        shownGroup.current = group;
        setValues((prev) => syncUntouchedFields(prev, previous, group));
    }, [group]);

    const { updateActiveFishGroup, isLoading } = useUpdateCageActiveFishGroup(
        cageId,
        clientId,
        group.id
    );

    const request = buildActiveFishGroupRequest(group, values);
    const fishCountInvalid = !isFishCountValid(values.fishCount);

    const handleChange =
        (field: keyof ActiveFishGroupFormValues) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const { value } = e.target;
            setValues((prev) => ({ ...prev, [field]: value }));
        };

    const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e.preventDefault();
        if (!request) return;
        setError(null);
        try {
            await updateActiveFishGroup(request);
            // The refetched group carries these same values, so the form reads as unchanged.
            setValues((prev) => ({ ...prev, name: prev.name.trim(), reason: '' }));
            ToastSuccess(t('activeFishGroup.updateSuccess'), 'cage-settings-fish-group-success');
        } catch (err) {
            setError(
                isActiveFishGroupChangedError(err)
                    ? t('activeFishGroup.groupChanged')
                    : apiErrorContent(err, undefined, (message) =>
                          t('activeFishGroup.updateError', { message })
                      )
            );
        }
    };

    return (
        <form>
            <Field label={t('activeFishGroup.name')} htmlFor={nameId}>
                <TextField
                    id={nameId}
                    type="text"
                    name="fishGroupName"
                    value={values.name}
                    onChange={handleChange('name')}
                    disabled={isLoading}
                    invalid={values.name.trim() === ''}
                />
            </Field>
            <Field
                label={t('activeFishGroup.fishCount')}
                htmlFor={fishCountId}
                hint={t('activeFishGroup.fishCountHint')}>
                <TextField
                    id={fishCountId}
                    type="number"
                    name="fishCount"
                    min={1}
                    step={1}
                    value={values.fishCount}
                    onChange={handleChange('fishCount')}
                    disabled={isLoading}
                    invalid={fishCountInvalid}
                />
            </Field>
            <Field label={t('activeFishGroup.reason')} htmlFor={reasonId}>
                <TextArea
                    id={reasonId}
                    name="reason"
                    rows={3}
                    placeholder={t('activeFishGroup.reasonPlaceholder')}
                    value={values.reason}
                    onChange={handleChange('reason')}
                    disabled={isLoading}
                />
            </Field>
            {error && (
                <Alert tone="danger" size="small">
                    {error}
                </Alert>
            )}
            {/* Pending until the refreshed name has reached the page, not just the PATCH. */}
            <Button
                disabled={isLoading || !request}
                icon={isLoading ? <Spinner /> : undefined}
                trackingId="cage_settings_fish_group_save"
                onClick={handleSubmit}>
                {tCommon('actions.save')}
            </Button>
        </form>
    );
}
